import { useCallback, useEffect, useState } from "react";
import { Clock, Loader2 } from "lucide-react";

import { cn } from "../../../lib/utils";

const HOUR_BUCKETS = Array.from({ length: 24 }, (_, i) => i);
const DAY_LABELS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

const formatCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
};

const BestPostTimePanel = ({ isDark, getBestPostTime, filters }) => {
  const [data, setData] = useState({ byDayOfWeek: [], byHour: [] });
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBestPostTime(filters);
      setData(res || { byDayOfWeek: [], byHour: [] });
    } catch (_e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [getBestPostTime, filters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const maxDayViews = Math.max(1, ...data.byDayOfWeek.map((d) => d.avgViews));
  const hourMap = new Map(data.byHour.map((h) => [h.hour, h]));
  const maxHourViews = Math.max(1, ...data.byHour.map((h) => h.avgViews));

  return (
    <div
      className={cn(
        "rounded-xl border",
        isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-white border-gray-200/70"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between px-4 py-3 border-b",
          isDark ? "border-white/[0.06]" : "border-gray-100"
        )}
      >
        <div className="flex items-center gap-2">
          <Clock className={cn("h-4 w-4", isDark ? "text-amber-400" : "text-amber-500")} />
          <h2 className={cn("text-[14px] font-semibold", isDark ? "text-white" : "text-gray-900")}>
            Beste postmoment
          </h2>
        </div>
      </div>

      {loading && data.byDayOfWeek.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className={cn("h-6 w-6 animate-spin", isDark ? "text-gray-500" : "text-gray-400")} />
        </div>
      ) : data.byDayOfWeek.length === 0 ? (
        <div className={cn("text-center py-12 text-[13px]", isDark ? "text-gray-500" : "text-gray-500")}>
          Nog geen data — voeg video's met een publicatiedatum toe.
        </div>
      ) : (
        <div className="p-4 space-y-5">
          {/* Day of week bars */}
          <div>
            <p
              className={cn(
                "text-[11px] font-semibold uppercase tracking-wider mb-2",
                isDark ? "text-gray-400" : "text-gray-500"
              )}
            >
              Per dag van de week (gemiddelde views)
            </p>
            <div className="grid grid-cols-7 gap-2">
              {DAY_LABELS.map((label, idx) => {
                const row = data.byDayOfWeek.find((d) => d.day === idx) || {
                  day: idx,
                  label,
                  avgViews: 0,
                  avgEngagement: 0,
                  count: 0,
                };
                const pct = (row.avgViews / maxDayViews) * 100;
                return (
                  <div key={idx} className="flex flex-col items-center gap-1">
                    <div
                      className={cn(
                        "w-full h-24 rounded-md flex items-end overflow-hidden",
                        isDark ? "bg-white/[0.04]" : "bg-gray-100"
                      )}
                    >
                      <div
                        className={cn(
                          "w-full transition-all rounded-md",
                          isDark ? "bg-amber-400/70" : "bg-amber-500/80"
                        )}
                        style={{ height: `${Math.max(2, pct)}%` }}
                      />
                    </div>
                    <span
                      className={cn(
                        "text-[10px] font-semibold",
                        isDark ? "text-gray-400" : "text-gray-600"
                      )}
                    >
                      {label}
                    </span>
                    <span
                      className={cn(
                        "text-[10px]",
                        isDark ? "text-gray-500" : "text-gray-500"
                      )}
                    >
                      {formatCount(row.avgViews)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hour of day bars */}
          <div>
            <p
              className={cn(
                "text-[11px] font-semibold uppercase tracking-wider mb-2",
                isDark ? "text-gray-400" : "text-gray-500"
              )}
            >
              Per uur van de dag (gemiddelde views)
            </p>
            <div className="grid grid-cols-12 gap-1">
              {HOUR_BUCKETS.map((h) => {
                const row = hourMap.get(h);
                const views = row?.avgViews ?? 0;
                const pct = (views / maxHourViews) * 100;
                return (
                  <div key={h} className="flex flex-col items-center gap-1">
                    <div
                      className={cn(
                        "w-full h-16 rounded-sm flex items-end overflow-hidden",
                        isDark ? "bg-white/[0.04]" : "bg-gray-100"
                      )}
                      title={`${h}:00 — ${formatCount(views)} views, ${row?.count || 0} video's`}
                    >
                      <div
                        className={cn(
                          "w-full transition-all rounded-sm",
                          isDark ? "bg-blue-400/70" : "bg-blue-500/80"
                        )}
                        style={{ height: `${Math.max(2, pct)}%` }}
                      />
                    </div>
                    <span
                      className={cn(
                        "text-[9px]",
                        isDark ? "text-gray-500" : "text-gray-500"
                      )}
                    >
                      {h}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BestPostTimePanel;
